import type {
  HeadlineResponse,
  OrganizationRankingResponseRankingResponseRelatedPagedData,
  OrgCompositeRankingResRelatedRankingResponseRelatedPagedData,
  PersonSportRankingResponseRelatedRankingResponseRelatedPagedData,
  PlayerItemResponsePagedData,
  RpmPickListPredictionAccuraciesRelatedPagedData,
  SiteResponse,
  TrendingListItemResponseTrendingListResponseRelatedPagedData,
} from '@on3/api';
import { ServiceLogo } from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { MobileMPU } from '@public/components/Ads/MobileMPU';
import { MPU } from '@public/components/Ads/MPU';
import { TeamModule } from '@public/components/Db/Hub/Modules/TeamModule';
import { CoverList } from '@public/components/Headlines/ArticleCover/CoverList';
import { FeedList } from '@public/components/Headlines/ArticleFeed/FeedList';
import { TopRankings as HighSchoolRankings } from '@public/components/HighSchool/Modules/TopRankings/TopRankings';
import { SEOHomeText } from '@public/components/Home/SEOHomeText';
import { HomeLayout } from '@public/components/Layouts/HomeLayout';
import { SignUpFooter } from '@public/components/Newsletter/SignUpFooter';
import { PredictionsModule } from '@public/components/Player/PredictionsModule';
import { PlayerRankingsModule } from '@public/components/Player/RankingsModule';
import { Connect } from '@public/components/Social/Connect';
import { FanSiteList } from '@public/components/Teams/FanSiteList';
import { LatestCommits } from '@public/components/Teams/LatestCommits';
import { rivalsData } from '@public/store/mocks/Connect.mock';
import { getArticleLists } from '@public/utils/articles';

import styles from './RivalsHome.module.scss';

export interface IRivalsPageProps {
  featuredArticles: HeadlineResponse[] | null;
  footballRecruitingArticles: HeadlineResponse[] | null;
  basketballRecruitingArticles: HeadlineResponse[] | null;
  latestCommits: PlayerItemResponsePagedData | null;
  highSchoolArticles: HeadlineResponse[] | null;
  highSchoolRankings: OrgCompositeRankingResRelatedRankingResponseRelatedPagedData | null;
  fanSites: SiteResponse[] | null;
  playerRankings: PersonSportRankingResponseRelatedRankingResponseRelatedPagedData | null;
  latestPredictions: RpmPickListPredictionAccuraciesRelatedPagedData | null;
  teamRecruitingRankings: OrganizationRankingResponseRankingResponseRelatedPagedData | null;
}

export const RivalsHome = ({
  featuredArticles,
  footballRecruitingArticles,
  basketballRecruitingArticles,
  highSchoolArticles,
  latestCommits,
  highSchoolRankings,
  fanSites,
  playerRankings,
  latestPredictions,
  teamRecruitingRankings,
}: IRivalsPageProps) => {
  const teamSites = fanSites?.filter((site) => site.live && site.inIndex);

  const [
    coverArticlesList,
    footballRecruitingArticlesList,
    basketballRecruitingArticlesList,
    highSchoolArticlesList,
  ] = getArticleLists({
    featuredArticles: {
      list: featuredArticles ?? [],
      sliceLimit: 3,
    },
    lists: [
      {
        list: footballRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: basketballRecruitingArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
      {
        list: highSchoolArticles ?? [],
        sliceLimit: 5,
        removeDuplicates: true,
      },
    ],
  });

  const latestCommitsList = latestCommits?.list ?? [];
  const showPredictions = !!latestPredictions?.list?.length;

  return (
    <div className={styles.layout}>
      <SEOHomeText className={styles.headline}>
        {/* TODO: Rivals - change to rivals logo */}
        <ServiceLogo type="on3" />
        <h1>
          Delivering trusted High School sports news, analysis, data, and
          insights to fans, athletes, schools, and brands
        </h1>
      </SEOHomeText>
      <HomeLayout>
        <div className={styles.center}>
          <div className={styles.inner}>
            {!!coverArticlesList.length && (
              <div className={styles.coverArticles}>
                <CoverList
                  authorColor="#6F747B"
                  backgroundColor="transparent"
                  categoryColor="var(--site-link-color)"
                  isHome
                  list={coverArticlesList}
                  titleColor="#1D252C"
                />
              </div>
            )}

            {!!teamRecruitingRankings && (
              <div className={styles.trending}>
                <TeamModule
                  selectedSport="football"
                  teamRanking={teamRecruitingRankings}
                />
              </div>
            )}

            <MobileMPU id="mobile_mpu_top" incontent />

            <div className={styles.articleLists}>
              {!!footballRecruitingArticlesList.length && (
                <FeedList
                  list={footballRecruitingArticlesList}
                  section="Football Recruiting"
                  viewAllLinks={[
                    {
                      url: '/rivals/category/football-recruiting/news/',
                      text: 'View All Football Recruiting',
                      title: 'Football Recruiting News',
                    },
                  ]}
                />
              )}

              {!!basketballRecruitingArticlesList.length && (
                <FeedList
                  list={basketballRecruitingArticlesList}
                  section="Basketball Recruiting"
                  viewAllLinks={[
                    {
                      url: '/rivals/category/basketball-recruiting/news/',
                      text: 'View All Basketball Recruiting',
                      title: 'Basketball Recruiting News',
                    },
                  ]}
                />
              )}

              {!!highSchoolArticlesList.length && (
                <FeedList
                  list={highSchoolArticlesList}
                  section="High School"
                  viewAllLinks={[
                    {
                      url: '/high-school/news/',
                      text: 'View All High School News',
                      title: 'High School News',
                    },
                  ]}
                />
              )}
            </div>
          </div>
        </div>

        <aside className={styles.right}>
          <div className={styles.inner}>
            <MPU id="mpu_top" />

            {!!latestCommitsList.length && (
              <LatestCommits list={latestCommitsList} />
            )}

            {!!playerRankings?.list?.length && (
              <PlayerRankingsModule playerRankings={playerRankings} />
            )}

            {showPredictions && (
              <PredictionsModule predictions={latestPredictions} />
            )}

            <MPU id="mpu_middle" />
            <MobileMPU id="mobile_mpu_middle" incontent />
          </div>
        </aside>

        <aside className={styles.left}>
          <div className={styles.inner}>
            {!!teamRecruitingRankings && (
              <div className={styles.trending}>
                <TeamModule
                  selectedSport="football"
                  teamRanking={teamRecruitingRankings}
                />
              </div>
            )}

            {!!teamSites && <FanSiteList list={teamSites} />}

            {highSchoolRankings && (
              <HighSchoolRankings rankings={highSchoolRankings} />
            )}

            <Connect list={rivalsData} title="On3 Connect" />
          </div>
        </aside>
      </HomeLayout>
      <SignUpFooter />
    </div>
  );
};
